import { supabase, isConfigured } from './supabase';
import type { Lead, LeadInput, LeadStatus } from '../types';

export interface SubmitLeadResult {
  success: boolean;
  lead_id?: string;
  error?: string;
}

/**
 * Submit a captured valuation lead.
 *
 * Two-step under the hood:
 *  1. valmate_submit_lead RPC — anon-callable, security-definer, writes
 *     the row and returns the new lead id.
 *  2. valmate-notify-lead edge function — fires the Resend email to
 *     the agent (or the ReHub fallback inbox in pre-launch).
 *
 * The notify call is best-effort: if Resend is down or misconfigured,
 * the lead is still safely persisted and the user sees a success state
 * (we'll catch the unsent notification in the dashboard or via Supabase
 * logs). We never tell the user "submission failed" because of a
 * missing email — that would lose the lead.
 */
export async function submitLead(lead: LeadInput): Promise<SubmitLeadResult> {
  if (!isConfigured) {
    return { success: false, error: 'Supabase not configured.' };
  }

  try {
    const { data, error } = await supabase.rpc('valmate_submit_lead', {
      p_uprn: lead.uprn,
      p_postcode: lead.postcode,
      p_address_snapshot: lead.address_snapshot,
      p_estimate: lead.estimate,
      p_estimate_low: lead.estimate_low,
      p_estimate_high: lead.estimate_high,
      p_confidence: lead.confidence,
      p_contact_name: lead.contact_name,
      p_contact_email: lead.contact_email,
      p_contact_phone: lead.contact_phone,
      p_best_time_to_call: lead.best_time_to_call ?? null,
      p_notes: lead.notes ?? null,
      p_agent_widget_id: lead.agent_widget_id ?? null,
    });

    if (error) {
      console.warn('valmate_submit_lead error', error);
      return { success: false, error: error.message };
    }
    const result = data as { success: boolean; lead_id?: string; error?: string } | null;
    if (!result?.success || !result.lead_id) {
      return {
        success: false,
        error: result?.error ?? 'Submission failed.',
      };
    }

    // Fire-and-forget notification.
    void supabase.functions
      .invoke('valmate-notify-lead', { body: { lead_id: result.lead_id } })
      .catch((err) => console.warn('valmate-notify-lead invoke threw', err));

    return { success: true, lead_id: result.lead_id };
  } catch (err: any) {
    console.warn('submitLead threw', err);
    return { success: false, error: err?.message ?? 'Unknown error' };
  }
}

/**
 * List the signed-in agent's own leads, newest first.
 *
 * Direct table query — relies on the "valmate_leads_agent_read" RLS
 * policy from 20260421130000_valmate_valuation_and_leads.sql which
 * scopes select to `agent_id = auth.uid()`. So we don't need an
 * agent_id filter in the query — RLS does it for us.
 */
export async function listMyLeads(): Promise<Lead[]> {
  if (!isConfigured) return [];
  try {
    const { data, error } = await supabase
      .from('valmate_leads')
      .select(
        'id, uprn, postcode, address_snapshot, estimate, estimate_low, estimate_high, confidence, contact_name, contact_email, contact_phone, best_time_to_call, notes, status, source, created_at, updated_at'
      )
      .order('created_at', { ascending: false })
      .limit(500);
    if (error) {
      console.warn('listMyLeads error', error);
      return [];
    }
    return (data ?? []) as Lead[];
  } catch (err) {
    console.warn('listMyLeads threw', err);
    return [];
  }
}

/**
 * Update a lead's pipeline status. Authenticated only — relies on
 * the "valmate_leads_agent_update" RLS policy so an agent can only
 * change their own rows.
 */
export async function updateLeadStatus(
  leadId: string,
  status: LeadStatus
): Promise<{ success: boolean; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Supabase not configured.' };
  try {
    const { error } = await supabase
      .from('valmate_leads')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', leadId);
    if (error) {
      console.warn('updateLeadStatus error', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err: any) {
    console.warn('updateLeadStatus threw', err);
    return { success: false, error: err?.message ?? 'Unknown error' };
  }
}
